import { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useGetTopByMinutesQuery } from '../services/leaderboardApi';
import RoleBadge, { getPrimaryRole } from '../components/RoleBadge';
import styles from '../css/TopUsers.module.css';

const DISCIPLINE_TABS = [
  { key: 'minutes', label: 'Минуты' },
  // Будущие дисциплины добавляются сюда
];

const PERIOD_TABS = [
  { key: 'prev_month',    label: 'Прошлый месяц' },
  { key: 'current_month', label: 'Текущий месяц' },
  { key: 'all_time',      label: 'Всё время'      },
];

const PER_PAGE = 100;

function formatMinutes(m) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min} мин`;
  if (min === 0) return `${h} ч`;
  return `${h} ч ${min} мин`;
}

function formatNumber(n) {
  return n.toLocaleString('ru-RU');
}

const TopUsers = () => {
  const [discipline, setDiscipline] = useState('minutes');
  const [period, setPeriod] = useState('current_month');
  const [page, setPage] = useState(1);
  const [allEntries, setAllEntries] = useState([]);
  const sentinelRef = useRef(null);

  const offset = (page - 1) * PER_PAGE;

  const { data, isFetching } = useGetTopByMinutesQuery({
    period,
    limit: PER_PAGE,
    offset,
  });

  // Сбросить список при смене периода или дисциплины
  useEffect(() => {
    setPage(1);
    setAllEntries([]);
  }, [period, discipline]);

  // Накапливать результаты для infinite scroll
  useEffect(() => {
    if (!data?.data) return;
    if (page === 1) {
      setAllEntries(data.data);
    } else {
      setAllEntries(prev => {
        const existingIds = new Set(prev.map(e => e.user_id));
        const newItems = data.data.filter(e => !existingIds.has(e.user_id));
        return [...prev, ...newItems];
      });
    }
  }, [data, page]);

  const totalPages = data?.meta?.total_pages ?? 1;
  const hasMore = page < totalPages;

  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && !isFetching && hasMore) {
      setPage(p => p + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  const showSkeleton = isFetching && allEntries.length === 0;
  const showEmpty = !isFetching && allEntries.length === 0;

  return (
    <div className={styles.topUsersPage}>
      <Helmet>
        <title>Топ пользователей — DoramaHype</title>
        <meta name="description" content="Топ пользователей DoramaHype по времени просмотра дорам" />
        <meta property="og:title" content="Топ пользователей — DoramaHype" />
        <meta property="og:url" content="https://doramahype.ru/top-users" />
      </Helmet>

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Топ пользователей</h1>
          <p className={styles.subtitle}>Самые активные участники сообщества</p>
        </div>

        {/* Дисциплины */}
        <div className={styles.disciplineTabs}>
          {DISCIPLINE_TABS.map(tab => (
            <button
              key={tab.key}
              className={`${styles.tab} ${discipline === tab.key ? styles.tabActive : ''}`}
              onClick={() => setDiscipline(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Периоды */}
        <div className={styles.periodTabs}>
          {PERIOD_TABS.map(tab => (
            <button
              key={tab.key}
              className={`${styles.periodTab} ${period === tab.key ? styles.periodTabActive : ''}`}
              onClick={() => setPeriod(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Таблица */}
        <div className={styles.leaderboard}>
          {showSkeleton ? (
            <div className={styles.loadingMore}>
              <div className={styles.spinner} />
            </div>
          ) : showEmpty ? (
            <div className={styles.emptyState}>
              Нет данных за выбранный период
            </div>
          ) : (
            <table className={styles.leaderboardTable}>
              <thead>
                <tr>
                  <th className={styles.thRank}>#</th>
                  <th className={styles.thUser}>Пользователь</th>
                  <th className={styles.thMinutes}>Минуты</th>
                </tr>
              </thead>
              <tbody>
                {allEntries.map(entry => (
                  <tr key={entry.user_id} className={styles.row}>
                    <td className={styles.rankCell}>
                      <span className={entry.rank <= 3 ? styles.rankTop : styles.rankNum}>
                        {entry.rank}
                      </span>
                    </td>
                    <td className={styles.userCell}>
                      <div className={styles.userInfo}>
                        {entry.avatar_url ? (
                          <img
                            src={entry.avatar_url}
                            alt=""
                            className={styles.avatar}
                          />
                        ) : (
                          <div className={styles.avatarFallback}>
                            {(entry.login || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <span className={styles.login}>
                          {entry.login || 'Пользователь'}
                          <RoleBadge role={getPrimaryRole(entry.roles ?? [])} />
                        </span>
                      </div>
                    </td>
                    <td className={styles.minutesCell}>
                      <span className={styles.minutesNum}>
                        {formatNumber(entry.minutes)}
                      </span>
                      <span className={styles.minutesLabel}>
                        {formatMinutes(entry.minutes)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div ref={sentinelRef} className={styles.sentinel} />

        {isFetching && allEntries.length > 0 && (
          <div className={styles.loadingMore}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TopUsers;
